/**
 * Provides the complete layout structure for authenticated users including:
 * - Fixed header with navigation and user menu
 * - Responsive collapsible sidebar with menu
 * - Content area with dynamic margins
 * - Notification indicators (orders, vendor notes)
 * - User profile dropdown menu
 * - Language selection modal
 * - Logout confirmation modal
 * - Subscription error notification bar
 * - WebSocket connection for real-time notifications
 * - White-label branding support
 *
 * Key Features:
 * - Redux connected for user state and actions
 * - React Router integration for navigation
 * - i18n support for internationalization
 * - Material-UI components for styling
 * - Ant Design for layout and modals
 * - Aphrodite for CSS-in-JS styling
 * - WebSocket for real-time notification updates
 * - Responsive design with mobile menu collapse
 * - White-label branding colors and logos
 * - Role-based menu item visibility
 * - Training links for white-label partners
 * - Dynamic colors from Color Utility
 *
 * Dependencies:
 * - React, Redux, React Router
 * - Ant Design, Material-UI
 * - i18next, Moment.js
 * - Reconnecting WebSocket
 * - Custom components and utilities
 * - Color Utility Functions
 */

import React from 'react';
import { Col, Dropdown, Layout, Menu } from 'antd';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../I18';
import './index.css';
import { MenuApp } from './menu';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import LocalStorage from '../../defined/localStorage';
import { Colors } from '../../themes/Colors';
import { SVGImageComponentReact } from '../../themes/Images';
import { ROUTER_KEY, ROUTER_NAME } from '../../router/routers';
import { getValueFromObjectByKeys, reloadApp, translate } from '../../helper';
import { ERROR_BAR_HEIGHT, heightOfHeader } from '../../utils/defined';
import withNavigate from '../../component/WithNavigate';
import { UserAction } from '../../redux/action';
import { logoutCleanup } from '../../utils/logoutCleanup';
import { store } from '../../redux/store';
import { css, StyleSheet } from 'aphrodite';
import ChangePassword from '../../component/ChangePassword';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { Box, Typography } from '@mui/material';
import Utils from '../../utils';
import poweredByLogo from '../../assets/image/png/PoweredByMove_logo-100.jpg';
import LogoutModal from './LogoutModal';
import LanguageSelectionModal from './LanguageSelectionModal';
import TwoFactorAuthModal from './TwoFactorAuthModal';
import Zendesk from '../../router/ZendeskConfig';
import { ROLES } from '../../common/roles';
import SubscriptionErrorBar from './SubscriptionErrorBar';
import { getFirstPermittedRoute } from '../../utils/getFirstPermittedRoute';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import OndemandVideoOutlinedIcon from '@mui/icons-material/OndemandVideoOutlined';
import HeaderIconBox from './HeaderIconBox';
import { getDropdownMenuItems } from './DropdownMenuItems';
import { messageManager, MESSAGE_TYPE } from '../../helper';
import {
  getColorBrightness,
  getTextColor,
  getOverlayColor,
  getAccentColor,
  darkenColor
} from './colorUtility';

const brand = JSON.parse(LocalStorage.getItem('branding'));

const TRAINING_LINKS = {
  'White Label Partner Admin': 'https://move2order.com/customer-support-portal/',
  OP: 'https://move2order.com/customer-support-portal/parts-pro/',
  SW: 'https://move2order.com/customer-support-portal/service-underwriter/'
};

const { Header, Content, Sider } = Layout;
const SubMenu = Menu.SubMenu;
const allKeyMenu = [];
const keySubMenu = 'subMenu-';

/**
 * Generate dynamic color palette from sidebar background color
 * Uses color utility functions for consistent color calculations
 */
const generateColorPalette = (bgColor) => {
  const brightness = getColorBrightness(bgColor);
  const textColor = getTextColor(bgColor);
  const accentColor = getAccentColor(bgColor);
  const overlayColor = getOverlayColor(bgColor, 0.08);
  const nonActiveMenuBg = darkenColor(bgColor,12);
  // Determine if background is light or dark
  const isDark = brightness <= 0.5;
  return {
    bgColor,
    brightness,
    textPrimary: textColor,
    nonActiveMenuBg,
    textSecondary: textColor,
    textMuted: getOverlayColor(textColor, 0.5),
    accent: accentColor,
    hoverBg: overlayColor,
    activeBg: 'rgba(255, 255, 255, 0.18)',
    activeText: '#ffffff',
    borderColor: 'rgba(255, 255, 255, 0.7)',
    activeShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.15)',
    isDark,
    divider: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)',
    dividerAccent: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    scrollbarThumb: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.12)',
    scrollbarThumbHover: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.25)'
  };
};

// Premium color palette (fallback)
const PREMIUM = {
  siderSolid: '#141729',
  accent: '#D4A547',
  textMuted: 'rgba(232, 236, 244, 0.35)',
  divider: 'rgba(255, 255, 255, 0.06)',
  dividerAccent: 'rgba(212, 165, 71, 0.2)',
};

const styles = StyleSheet.create({
  iconNoNotification: {
    width: 'calc(var(--fs-xl)*1.5)',
    height: 'calc(var(--fs-xl)*1.32)',
    marginRight: 'var(--sp-xs)',
    cursor: 'pointer'
  },
  siderInner: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 'calc(var(--sp-md2)*1.2) calc(var(--sp-md2)*1)',
    borderBottom: `1px solid rgba(0, 0, 0, 0.08)`,
    position: 'relative',
    minHeight: 'calc(var(--fs-hero)*2.2)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden'
  },
  logoInner: {
    width: 'calc(var(--fs-hero)*4.5)',
    height: 'calc(var(--fs-hero)*2.5)',
    borderRadius: 'calc(var(--sp-md2)*0.5)',
    backgroundColor: '#fff',
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: `inset 0 1px 2px rgba(255,255,255,0.95), inset 0 -0.5px 1px rgba(0,0,0,0.04), 
                 0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.1), 
                 0 8px 24px rgba(0,0,0,0.12)`,
    padding: 'calc(var(--sp-md2)*0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 1000,
    boxSizing: 'border-box'
  },
  menuScrollArea: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    padding: 'calc(var(--sp-xxs)*3) 0',
    backgroundImage: `
      linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 20%),
      linear-gradient(180deg, transparent 80%, rgba(0,0,0,0.02) 100%)
    `,
    backgroundAttachment: 'fixed'
  },
  footerSection: {
    borderTop: `1px solid ${PREMIUM.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'calc(var(--sp-xxs)*1.179245) var(--sp-md2)'
  },
  poweredByContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'calc(var(--sp-xxs)*2) calc(var(--sp-md2)*1.132075)',
    borderRadius: 'calc(var(--sp-xxs)*2)',
    background: 'white',
    width: '100%',
    transition: 'all 0.3s ease'
  },
});

const tooltipOverlayStyle = {
  fontSize: 'calc(var(--fs-xs)*1.32075)',
  padding: 'var(--sp-sm) var(--sp-md2)',
  color: '#fff',
  fontWeight: 500,
  letterSpacing: '0.2px',
  background: 'transparent',
  boxShadow: 'none'
};

/**
 * Title - Menu item title component with translation and disabled state support
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Translation key
 * @param {boolean} props.disabled - Whether item is disabled
 * @param {Object} props.style - Custom inline styles
 * @returns {React.ReactNode} - Styled menu title text
 */
const Title = ({ children, disabled, style }) => {
  const { t } = useTranslation();
  return (
    <>
      {disabled ? (
        <span className="nav-text" style={{ color: 'grey', ...style }}>
          {t(children)}
        </span>
      ) : (
        <span className="nav-text" style={style}>
          {t(children)}
        </span>
      )}
    </>
  );
};

/**
 * PremiumMenuItem - A single premium-styled menu item with dynamic colors from Color Utility
 */
const PremiumMenuItem = ({ item, isActive, onClick, onNavigate, colors }) => {
  const IconFinal = item.icon;
  const isLogout = item.route === ROUTER_KEY.logout;

  // Use provided colors or fall back to PREMIUM defaults
  const themeColors = colors || PREMIUM;

  const content = (
    <div
      className={`premium-menu-item ${isActive ? 'premium-menu-item--active' : ''}`}
      style={{
        margin: 'calc(var(--sp-md2)*0.5) calc(var(--sp-md2)*0.75)',
        borderRadius: 'calc(var(--sp-md2)*0.75)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
      {/* Background card effect */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: isActive ? 'transparent' : 'transparent',
          pointerEvents: 'none',
          borderRadius: 'calc(var(--sp-md2)*0.75)',
          transition: 'all 0.3s ease'
        }}
      />

      <div
        className={`premium-menu-link ${isActive ? 'premium-menu-link--active' : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: 'calc(var(--sp-md2)*0.8) calc(var(--sp-md2)*1)',
          borderRadius: 'calc(var(--sp-md2)*0.75)',
          textDecoration: 'none',
          gap: 'calc(var(--sp-md2)*0.75)',
          cursor: 'pointer',
          background: isActive ? '#ffffff' : `${themeColors?.nonActiveMenuBg}`,
          border: isActive
            ? `1.5px solid ${themeColors?.bgColor || '#141729'}`
            : `1.5px solid ${themeColors?.bgColor || '#141729'}`,
          boxShadow: isActive
            ? `
              0 8px 24px rgba(0, 0, 0, 0.15),
              inset 0 1px 2px rgba(255, 255, 255, 0.2),
              inset 0 -1px 2px rgba(0, 0, 0, 0.08)
            `
            : `
              0 2px 8px rgba(0, 0, 0, 0.08),
              inset 0 1px 1px rgba(255, 255, 255, 0.05)
            `,
          position: 'relative',
          outline: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
        {/* ── Icon wrapper ── */}
        <Box
          className="premium-icon-wrapper"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'calc(var(--fs-xl)*1.6)',
            height: 'calc(var(--fs-xl)*1.6)',
            minWidth: 'calc(var(--fs-xl)*1.6)',
            borderRadius: 'calc(var(--sp-md2)*0.5)',
            flexShrink: 0,
            background:
              isActive && `linear-gradient(135deg, ${item.color}dd 0%, ${item.color}99 100%)`,
            border: `1.5px solid ${isActive && `${item.color}66`}`,
            boxShadow:
              isActive &&
              `
                0 4px 16px ${item.color}40,
                inset 0 1px 2px rgba(255, 255, 255, 0.4)
              `,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: `linear-gradient(135deg, ${item.color}dd 0%, ${item.color}99 100%)`,
              border: `1.5px solid ${item.color}66`,
              boxShadow: `
              0 6px 20px ${item.color}55,
              inset 0 1px 2px rgba(255,255,255,0.4)
            `,
              transform: 'scale(1.08)'
            }
          }}>
          {IconFinal && (
            <IconFinal
              className="menu-icon premium-menu-icon"
              style={{
                color: '#ffffff',
                fontSize: 'calc(var(--fs-xl)*1.2)',
                transition: 'all 0.3s ease',
                filter: isActive
                  ? 'drop-shadow(0 0 6px rgba(0,0,0,0.2))'
                  : 'drop-shadow(0 0 2px rgba(0,0,0,0.1))'
              }}
            />
          )}
        </Box>

        {/* ── Label text ── */}
        <span
          className="premium-menu-text"
          style={{
            fontSize: 'var(--fs-sm2) !important',
            color: isActive ? themeColors?.bgColor || '#141729' : '#ffffff',
            fontWeight: isActive ? 800 : 600,
            transition: 'all 0.3s ease',
            letterSpacing: '0.3px',
            whiteSpace: 'nowrap',
            textShadow: isActive ? 'none' : 'none',
            flex: 1
          }}>
          {translate(item.title)}
        </span>
      </div>
    </div>
  );

  if (isLogout) {
    return <div onClick={onClick}>{content}</div>;
  }

  return (
    <Link
      to={'/' + item.route}
      onClick={onNavigate}
      className="premium-menu-router-link"
      style={{
        textDecoration: 'none',
        outline: 'none',
        boxShadow: 'none'
      }}>
      {content}
    </Link>
  );
};

/**
 * LayoutApp - Main authenticated user layout component
 *
 * Manages the complete layout structure for authenticated users:
 * - Fixed header with branding, notifications, and user menu
 * - Collapsible sidebar with navigation menu
 * - Main content area with responsive margins
 * - Real-time notification updates via WebSocket
 * - White-label branding support
 * - Role-based menu visibility
 * - Language and logout modals
 */
class LayoutApp extends React.Component {
  constructor(props) {
    super(props);
    // State management for layout and UI elements
    this.state = {
      hasNotifications: false,
      selectedKeys: ['orders'],
      openKeys: [],
      collapsed: window.outerWidth <= 768,
      showChangePassword: false,
      openLanguageSelector: false,
      openLogoutModal: false,
      showErrorBar: false,
      errorBarDismissed: false,
      showRolesPermission: true,
      hoveredItem: null,
      show2FAModal: false,
      is2FAEnabled: false,
      is2FALoading: false
    };
    this.socket = null;
  }

  /**
   * closeChangePasswordModal - Hide password change modal
   */
  closeChangePasswordModal = () => {
    this.setState({
      showChangePassword: false
    });
  };

  /**
   * handle2FAToggle - Open 2FA confirmation modal
   */
  handle2FAToggleClick = () => {
    this.setState({
      show2FAModal: true
    });
  };

  /**
   * close2FAModal - Close 2FA confirmation modal
   */
  close2FAModal = () => {
    this.setState({
      show2FAModal: false
    });
  };

  /**
   * confirm2FAToggle - Confirm and execute 2FA toggle
   */
  confirm2FAToggle = async () => {
    const { userInfo, toggle2FA, getUserProfile } = this.props;
    const currentStatus = userInfo?.user?.is_two_factor_auth_enabled || false;
    const newStatus = !currentStatus;

    this.setState({ is2FALoading: true });

    try {
      await toggle2FA({ enabled: newStatus });

      // Refresh user profile
      const userId = getValueFromObjectByKeys(userInfo, ['user', 'id']);
      if (userId) {
        await getUserProfile(userId);
      }

      messageManager({
        type: MESSAGE_TYPE.success,
        messageShow: newStatus
          ? translate('2faEnabledSuccess')
          : translate('2faDisabledSuccess')
      });

      this.setState({
        show2FAModal: false,
        is2FALoading: false
      });
    } catch (error) {
      messageManager({
        type: MESSAGE_TYPE.error,
        messageShow: error?.message || translate('2faToggleError')
      });
      this.setState({ is2FALoading: false });
    }
  };

  /**
   * handleCloseLogoutModal - Close logout modal and restore Zendesk messenger
   */
  handleCloseLogoutModal = () => {
    this.setState({
      openLogoutModal: false
    });
    if (window.zE) {
      window.zE('messenger', 'show');
    }
  };

  formatMenu = (menu = []) => {
    const { userInfo, permissions: reduxPermissions } = this.props;

    const profilePermissions = userInfo?.profile?.user?.group?.permissions;
    const activePermissions =
      Array.isArray(profilePermissions) && profilePermissions.length > 0
        ? profilePermissions
        : reduxPermissions;

    const hasPermission = (menuItem) => {
      if (!menuItem.permissionCodename) return true;
      return activePermissions?.some((perm) => perm.codename === menuItem.permissionCodename);
    };

    const hasRoleAccess = userInfo?.user?.role_management_access === true;
    const accessFilteredMenu = menu.filter((item) =>
      item.requireRoleManagementAccess ? hasRoleAccess : true
    );

    const isSuperUser = userInfo?.profile?.user?.is_superuser || false;
    if (isSuperUser) {
      return accessFilteredMenu
        .filter((item) => item.isSuperUser)
        .map((item) => ({ ...item, route: item.route }));
    }
    if (userInfo?.profile?.team_member) {
      return accessFilteredMenu
        .filter(hasPermission)
        .map((item) => ({ ...item, route: item.route }));
    }

    const nonParentAccountMenu = accessFilteredMenu.filter((item) =>
      item?.isChildBranding
        ? userInfo?.profile?.manage_branding && !userInfo?.profile?.branding_locked
        : !item?.isParentAccountOnly
    );

    const isWhiteLabelAdmin = userInfo?.profile?.user?.role === ROLES.WHITE_LABEL_PARTNER_ADMIN;
    return (isWhiteLabelAdmin ? accessFilteredMenu : nonParentAccountMenu).map((item) => ({
      ...item,
      route: item.route
    }));
  };

  /**
   * Renders premium menu items using the custom PremiumMenuItem component
   * @param {Array} menu - Menu items array
   * @param {Object} colorPalette - Dynamic color palette
   */
  getPremiumMenu = (menu = [], colorPalette = null) => {
    return menu
      .filter((item) => !item.hide)
      .map((itemMenu, index) => {
        if (itemMenu.items) {
          if (
            itemMenu.items.length !== itemMenu.items.filter((elem) => elem.hide === true).length
          ) {
            return (
              <div key={`section-${index}`}>
                <div
                  style={{
                    height: 'calc(var(--fs-xs)*0.0943396)',
                    background: `linear-gradient(90deg, transparent, ${
                      colorPalette?.divider || PREMIUM.divider
                    }, transparent)`,
                    margin: 'calc(var(--sp-xxs)*2) calc(var(--sp-lg)*1)'
                  }}
                />
                <div
                  style={{
                    padding:
                      'calc(var(--sp-md2)*1.50943) calc(var(--sp-md2)*2.26415) calc(var(--sp-md2)*0.754716)',
                    fontSize: 'var(--fs-xs)',
                    fontWeight: 700,
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                    color: colorPalette?.textMuted || PREMIUM.textMuted,
                    userSelect: 'none'
                  }}>
                  {translate(itemMenu.title)}
                </div>
                {this.getPremiumMenu(itemMenu.items, colorPalette)}
              </div>
            );
          }
          return null;
        }

        const isMenuActive = this.state.selectedKeys[0].includes(itemMenu.route);

        return (
          <PremiumMenuItem
            key={itemMenu.route}
            item={itemMenu}
            isActive={isMenuActive}
            colors={colorPalette}
            onClick={itemMenu.route === ROUTER_KEY.logout ? this.logout : undefined}
            onNavigate={() => {
              this.props.resetAllPages && this.props.resetAllPages();
            }}
          />
        );
      });
  };

  getMenu = (menu = [], route) => {
    return menu.map((itemMenu, index) => {
      if (!itemMenu.hide) {
        if (itemMenu.items) {
          const key = route
            ? keySubMenu + route + '-' + itemMenu.route
            : keySubMenu + itemMenu.route;
          if (
            itemMenu.items.length !== itemMenu.items.filter((elem) => elem.hide === true).length
          ) {
            return (
              <SubMenu key={key} title={<span>{itemMenu.title}</span>}>
                {this.getMenu(itemMenu.items, itemMenu.route)}
              </SubMenu>
            );
          }
        } else {
          const isMenuActive = this.state.selectedKeys[0].includes(itemMenu.route);
          const IconFinal = itemMenu.icon;

          return (
            <Menu.Item
              className={'item-menu' + (route ? 'item-sub-menu' : '')}
              key={'/' + itemMenu.route}
              style={{
                height: 'calc(var(--fs-xs)*5.66)',
                marginBottom: 'calc(var(--sp-xs)*1.88) !important'
              }}>
              {itemMenu.route === ROUTER_KEY.logout ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                  <div
                    onClick={this.logout}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: 'calc(var(--fs-xl)*1.88)'
                    }}>
                    {IconFinal ? <IconFinal className="menu-icon" /> : null}
                    <Title>{itemMenu.title}</Title>
                  </div>
                </div>
              ) : (
                <Link
                  onClick={() => {
                    this.props.resetAllPages && this.props.resetAllPages();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #fff',
                    height: 'calc(var(--fs-xl)*2.358)',
                    borderRadius: 'var(--sp-xs)',
                    fontWeight: 'bold',
                    backgroundColor: isMenuActive ? '#FBB516' : 'transparent',
                    color: '#fff !important'
                  }}
                  to={'/' + itemMenu.route}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingLeft: '10%'
                    }}>
                    {IconFinal ? <IconFinal className="menu-icon" /> : null}
                    <div
                      style={{ color: '#fff !important', fontSize: 'calc(var(--fs-sm2) * 1.015)' }}>
                      {translate(itemMenu.title)}
                    </div>
                  </div>
                </Link>
              )}
            </Menu.Item>
          );
        }
      }
    });
  };

  getAllKeyMenu = (menu = [], route) => {
    menu.map((itemMenu) => {
      if (!itemMenu.hide) {
        if (itemMenu.items) {
          if (
            itemMenu.items.length !== itemMenu.items.filter((elem) => elem.hide === true).length
          ) {
            this.getAllKeyMenu(itemMenu.items, itemMenu.route);
          }
        } else {
          if (allKeyMenu.indexOf(itemMenu.route) === -1) {
            allKeyMenu.push(itemMenu.route);
          }
        }
      }
    });
    return allKeyMenu;
  };

  toggle = () => {
    this.setState({
      collapsed: !this.state.collapsed
    });
  };

  showOverflow = () => {
    const routeUrl = this.props?.location?.pathname;
    return !(routeUrl === '/privacy-policy' || routeUrl === '/terms-and-conditions');
  };

  async componentDidMount() {
    this.getSelectedKeyAndOpenKey();
    const { userInfo, getUserProfile } = this.props;
    this.getUpdates();
    if (userInfo && LocalStorage.getItem(LocalStorage.DEFINE_KEY.ACCESS_TOKEN)) {
      const userId = getValueFromObjectByKeys(userInfo, ['user', 'id']);
      getUserProfile && getUserProfile(userId);
    }
    const dismissed = localStorage.getItem('SUBSCRIPTION_ERROR_DISMISSED');
    const activeSubscription = this.props.userInfo?.user?.active_subscription;
    const role = this.props.userInfo?.user?.role;
    const shouldShowErrorBar =
      activeSubscription === false && Object.values(ROLES).includes(role) && !dismissed;

    if (shouldShowErrorBar) {
      this.setState({ showErrorBar: true });
    }

  }

  handleDismissErrorBar = () => {
    localStorage.setItem('SUBSCRIPTION_ERROR_DISMISSED', 'true');
    this.setState({ showErrorBar: false });
  };

  getSubscriptionErrorMessage = () => {
    const role = this.props.userInfo?.user?.role;

    if (role === 'White Label Partner Admin') {
      return "Please check your subscription. If it's not active, you will not be able to receive any orders.";
    }

    if (role === 'Child Account') {
      return "Please get in touch with your Admin to check their subscription. If it's not active, you will not be able to receive any orders.";
    }

    return '';
  };

  componentWillUnmount() {
    if (this.socket.readyState === 1) {
      this.socket.close();
    }
  }

  getUpdates = () => {
    this.props.getUnreadNotifCount();
    const {
      userInfo: { token }
    } = this.props;
    this.socket = new ReconnectingWebSocket(
      `wss://${Utils.constants.API_URL.replace(
        'https://',
        ''
      )}ws/home/notifications/?token=${token}`
    );
    this.socket.addEventListener('open', () => {
      if (window.location.pathname === '/notification') {
        this.props.getNotifications(false);
      }
      if (window.location.pathname === '/vendor-notes-notification') {
        this.props.getNotifications(true);
      }
    });

    this.socket.onmessage = (event) => {
      let newData = JSON.parse(event?.data)?.data;
      let data = {
        unread_count: newData?.unread_count,
        unread_vendornote_count: newData?.unread_vendornote_count
      };
      this.props.updateUnreadNotifCount({ data });
      if (window.location.pathname === '/notification') {
        this.props.getNotifications(false);
      }
      if (window.location.pathname === '/vendor-notes-notification') {
        this.props.getNotifications(true);
      }
    };
  };

  componentWillReceiveProps(nextProps) {
    this.getSelectedKeyAndOpenKey();
  }

  getSelectedKeyAndOpenKey = () => {
    let hash = window.location.pathname;
    let parentKeys = [keySubMenu + hash.split('/')[1]];
    if (hash.split('/').length > 3) {
      parentKeys = [
        keySubMenu + hash.split('/')[1],
        keySubMenu + hash.split('/')[1] + '/' + hash.split('/')[2]
      ];
    }
    hash = hash.indexOf('?') !== -1 ? hash.split('?')[0] : hash;
    let hashFormat = hash;
    if (allKeyMenu.indexOf(hashFormat) === -1) {
      let arrHashFormat = hashFormat.split('/');
      if (arrHashFormat.length > 1) {
        allKeyMenu.map((keyMenu) => {
          if (
            hashFormat.indexOf('/list') === -1 &&
            keyMenu.indexOf(hashFormat.replace(arrHashFormat[arrHashFormat.length - 1], '')) !== -1
          ) {
            hash = '/' + keyMenu;
          }
        });
      }
    }

    if (hash === '/order-detail') {
      hash = '/order';
    }
    if (hash === '/create-profile' || hash === '/privacy' || hash === '/terms') {
      hash = '/settings';
    }
    this.setState({
      selectedKeys: [hash],
      openKeys: parentKeys
    });
  };

  logout = async () => {
    try {
      logoutCleanup({
        dispatch: store.dispatch,
        navigate: this.props.navigate,
        logoutAction: UserAction.logoutAction,
        redirectUrl: '/login'
      });
    } catch (error) {
      reloadApp();
    }
  };

  render() {
    const { selectedKeys, openKeys, collapsed } = this.state;
    const { userInfo, notificationsObj } = this.props;

    const is2FAEnabled = userInfo?.user?.is_two_factor_auth_enabled || false;

    const menuItems = getDropdownMenuItems(
      {
        onProfileClick: () => {
          this.props.navigate && this.props.navigate(ROUTER_NAME.createProfile);
        },
        onPasswordClick: () => {
          this.setState({ showChangePassword: true });
        },
        on2FAClick: () => {
          this.handle2FAToggleClick();
        },
        onPrivacyClick: () => {
          this.props.navigate && this.props.navigate(ROUTER_NAME.privacyPrivate);
        },
        onTermsClick: () => {
          this.props.navigate && this.props.navigate(ROUTER_NAME.termsPrivate);
        },
        onLanguageClick: () => {
          this.setState({ openLanguageSelector: true });
        },
        onSupportClick: () => {
          window.zE('messenger', 'show');
        },
        onLogoutClick: () => {
          this.setState({ openLogoutModal: true });
        }
      },
      userInfo,
      { is2FAEnabled }
    );

    const menuUser = <Menu className="dropdown-logout" items={menuItems} />;

    const formattedMenu = this.formatMenu(MenuApp());
    const role = userInfo?.profile?.user?.role || userInfo?.user?.role || '';
    const isTrainingVisible = ['White Label Partner Admin', 'OP', 'SW'].includes(role);
    const { whitelabel, permissions: reduxPermissions } = this.props;
    const isTeamMember = userInfo?.profile?.team_member;
    const profilePermissions = userInfo?.profile?.user?.group?.permissions;
    const activePermissions =
      Array.isArray(profilePermissions) && profilePermissions.length > 0
        ? profilePermissions
        : reduxPermissions;
    const logoRoute = getFirstPermittedRoute({ isTeamMember, permissions: activePermissions });
    const TOTAL_TOP_OFFSET = this.state.showErrorBar
      ? `calc(${heightOfHeader} + ${ERROR_BAR_HEIGHT})`
      : heightOfHeader;

    // Get sidebar background color from whitelabel/brand or use premium default
    const sidebarBrandColor =
      whitelabel?.web_side_banner_color || brand?.web_side_banner_color || Colors.bgSider;

    // Generate dynamic color palette using utility functions
    const colorPalette = generateColorPalette(sidebarBrandColor);

    return (
      <div>
        {this.state.openLanguageSelector && (
          <LanguageSelectionModal
            isOpen={this.state.openLanguageSelector}
            onClose={() => {
              this.setState({
                openLanguageSelector: false
              });
            }}
          />
        )}
        <Zendesk defer />
        <Layout
          style={{
            backgroundSize: '100%',
            minHeight: '100vh',
            backgroundPosition: 'bottom',
            position: 'relative',
            zIndex: 2,
            backgroundColor: '#fff'
          }}>
          <Sider
            className={`premium-sider ${collapsed ? 'premium-sider--collapsed' : ''}`}
            style={{
              overflow: 'hidden',
              height: `calc(100vh - ${TOTAL_TOP_OFFSET})`,
              position: 'fixed',
              left: 0,
              zIndex: 9999,
              top: TOTAL_TOP_OFFSET,
              background: 'transparent',
              borderRight: `1px solid ${colorPalette.divider}`,
              boxShadow: '4px 0 32px rgba(0, 0, 0, 0.2)'
            }}
            breakpoint="lg"
            collapsedWidth="0"
            collapsed={collapsed}
            trigger={null}
            width={
              i18n.language === 'es' ? 'calc(var(--fs-xl)*14.56)' : 'calc(var(--fs-xl)*10.849)'
            }
            onCollapse={(collapsed, type) => {
              this.setState({
                collapsed
              });
            }}>
            <div
              className={css(styles.siderInner)}
              style={{
                background: `
                  linear-gradient(
                    180deg,
                    ${sidebarBrandColor}f0 0%,
                    ${sidebarBrandColor}e8 35%,
                    ${sidebarBrandColor}f5 100%
                  ),
                  linear-gradient(
                    90deg,
                    rgba(255, 255, 255, 0.08) 0%,
                    transparent 50%,
                    rgba(0, 0, 0, 0.06) 100%
                  )
                `,
                backgroundAttachment: 'fixed',
                position: 'relative'
              }}>
              {/* Premium glow layer */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '100px',
                  background: `linear-gradient(
                    180deg,
                    rgba(255, 255, 255, 0.1) 0%,
                    transparent 100%
                  )`,
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              />
              <Link
                to={logoRoute}
                onClick={() => {
                  this.props.resetAllPages && this.props.resetAllPages();
                }}
                // className="premium-logo-link"
                style={{ textDecoration: 'none' }}>
                <div
                  className={css(styles.logoContainer)}
                  style={{
                    background: `
                      linear-gradient(
                        135deg,
                        ${sidebarBrandColor}f5 0%,
                        ${sidebarBrandColor}ed 50%,
                        ${sidebarBrandColor}f8 100%
                      ),
                      linear-gradient(
                        90deg,
                        rgba(255, 255, 255, 0.08) 0%,
                        transparent 100%
                      )
                    `,
                    boxShadow: `
                      0 4px 20px rgba(0, 0, 0, 0.08),
                      inset 0 1px 0 rgba(255, 255, 255, 0.1),
                      inset 0 -1px 0 ${colorPalette.divider}
                    `
                  }}>
                  <div className={css(styles.logoInner)}>
                    {whitelabel?.web_logo || brand?.web_logo ? (
                      <img
                        src={whitelabel?.web_logo || brand?.web_logo}
                        alt="logo"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          borderRadius: 'calc(var(--sp-md2)*0.4)'
                        }}
                      />
                    ) : (
                      <SVGImageComponentReact.LogoMoveMenu />
                    )}
                  </div>
                </div>
              </Link>

              <div
                style={{
                  height: 'calc(var(--fs-xs)*0.1886792)',
                  background: `linear-gradient(90deg, transparent, ${colorPalette.dividerAccent}, transparent)`,
                  opacity: 1
                }}
              />

              {/* Menu Section */}
              <div className={`${css(styles.menuScrollArea)} premium-scrollbar`}>
                {/* Premium Menu Items */}
                {this.getPremiumMenu(formattedMenu, colorPalette)}
              </div>

              <div
                style={{
                  height: 'calc(var(--fs-xs)*0.1886792)',
                  background: `linear-gradient(90deg, transparent, ${colorPalette.accent}, transparent)`,
                  opacity: 1
                }}
              />

              <div
                className={css(styles.footerSection)}
                style={{
                  background: `
                    linear-gradient(
                      180deg,
                      ${sidebarBrandColor}f5 0%,
                      ${sidebarBrandColor}ed 100%
                    ),
                    linear-gradient(
                      90deg,
                      transparent 0%,
                      rgba(255, 255, 255, 0.06) 100%
                    )
                  `,
                  borderTopColor: colorPalette.divider,
                  boxShadow: `
                    inset 0 1px 0 rgba(255, 255, 255, 0.1),
                    0 -4px 20px rgba(0, 0, 0, 0.08)
                  `
                }}>
                <div className={css(styles.poweredByContainer)}>
                  <img
                    src={poweredByLogo}
                    style={{
                      width: 'auto',
                      height: '100%',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      opacity: 0.85
                    }}
                    alt="powered-by"
                    className="premium-powered-logo"
                  />
                </div>
              </div>
            </div>
          </Sider>
          <Layout
            style={{
              overflowY: this.showOverflow() ? 'auto' : 'hidden',
              marginLeft: collapsed ? 0 : '0px',
              backgroundColor: Colors.transparent,
              maxHeight: '100vh'
            }}>
            <Header
              style={{
                background: `
                    linear-gradient(
                        135deg,
                        ${
                          whitelabel?.web_top_banner_color ||
                          brand?.web_top_banner_color ||
                          Colors.bgSider
                        }ee 0%,

                        ${
                          whitelabel?.web_top_banner_color ||
                          brand?.web_top_banner_color ||
                          Colors.bgSider
                        }f5 45%,

                        ${
                          whitelabel?.web_top_banner_color ||
                          brand?.web_top_banner_color ||
                          Colors.bgSider
                        }ff 100%
                    )
                `,
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                padding: 0,
                height: heightOfHeader,
                position: 'fixed',
                width: '100%',
                top: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                justifyContent: 'space-between',
                boxShadow: `
                  0 8px 32px rgba(0,0,0,0.18),
                  inset 0 -1px 0 rgba(255,255,255,0.04),
                  inset 0 1px 0 rgba(255,255,255,0.05)
                `,
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              id="header-private-parent">
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: `
                      linear-gradient(
                        90deg,
                        rgba(255,255,255,0.04) 0%,
                        transparent 30%,
                        transparent 70%,
                        rgba(255,255,255,0.03) 100%
                      )
                    `,
                  pointerEvents: 'none'
                }}
              />
              <Col className="txt-left" span={8} xs={24} sm={24} md={8}>
                <div
                  style={{ paddingLeft: 'var(--sp-md2)' }}
                  onClick={() => {
                    this.setState({
                      collapsed: !collapsed
                    });
                  }}
                  className="flex-row-center height-100">
                  {collapsed ? (
                    <MenuUnfoldOutlined
                      style={{
                        fontSize: 'calc(var(--fs-xl) * 1.13)',
                        color: Colors.white,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    />
                  ) : (
                    <MenuFoldOutlined
                      style={{
                        fontSize: 'calc(var(--fs-xl) * 1.13)',
                        color: Colors.white,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    />
                  )}
                  <Typography
                    variant="p"
                    fontWeight={'600'}
                    color={'#fff'}
                    fontSize={'calc(var(--fs-xs)*1.50943)'}
                    sx={{
                      paddingLeft: 'calc(var(--sp-lg)*1.2)',
                      textTransform: 'capitalize',
                      fontWeight: 700,
                      letterSpacing: '-0.03em',
                      color: '#f8fafc',
                      opacity: 0.95,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = Colors.primary;
                      e.target.style.textShadow = '0 0 12px rgba(250, 248, 139, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#fff';
                      e.target.style.textShadow = 'none';
                    }}
                    onClick={(evt) => evt.stopPropagation()}>
                    {userInfo?.user?.vendor?.business_name || 'N/A'}
                  </Typography>
                </div>
              </Col>
              <Col className="wrap-current-user" span={16} xs={24} sm={24} md={16}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 'var(--sp-sm) var(--sp-md2)',
                    backdropFilter: 'blur(16px)'
                  }}>
                  {isTrainingVisible &&
                    (() => {
                      const trainingHref = TRAINING_LINKS[role];
                      return (
                        <HeaderIconBox
                          tooltip={translate('platformTraining')}
                          overlayInnerStyle={tooltipOverlayStyle}>
                          <Link
                            to={trainingHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              color: '#fff'
                            }}
                            onClick={(e) => e.stopPropagation()}>
                            <OndemandVideoOutlinedIcon
                              sx={{ fontSize: 'calc(var(--fs-xl)*1.32)' }}
                            />
                          </Link>
                        </HeaderIconBox>
                      );
                    })()}
                  <HeaderIconBox
                    tooltip={translate('vendorNotifications')}
                    badgeCount={notificationsObj?.unread_vendornote_count}
                    overlayInnerStyle={tooltipOverlayStyle}>
                    <Link
                      className="flex relative"
                      to={ROUTER_NAME.vendorNotesNotification}
                      state={{ vendor_notes: true }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative'
                      }}>
                      <SVGImageComponentReact.IconNotes
                        className={css(styles.iconNoNotification)}
                      />
                    </Link>
                  </HeaderIconBox>
                  <HeaderIconBox
                    tooltip={translate('orderNotifications')}
                    badgeCount={notificationsObj?.unread_count}
                    overlayInnerStyle={tooltipOverlayStyle}>
                    <Link
                      className="flex relative"
                      to={ROUTER_NAME.notification}
                      state={{ vendor_notes: false }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        color: '#fff'
                      }}>
                      <NotificationsOutlinedIcon sx={{ fontSize: 'calc(var(--fs-xl)*1.32)' }} />
                    </Link>
                  </HeaderIconBox>
                </div>
                <Dropdown
                  className="container-dropdown-logout cursor-pointer"
                  overlay={menuUser}
                  overlayStyle={{ marginTop: heightOfHeader }}
                  trigger={['click']}>
                  <div className="user-dropdown-link">
                    <div
                      className="layout-username"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 'calc(var(--sp-lg) * 1.1)',
                        backdropFilter: 'blur(14px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        gap: 'calc(var(--sp-md2) * 0.8)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 'calc(var(--fs-xl)*1.886)',
                          height: 'calc(var(--fs-xl)*1.886)',
                          borderRadius: '50%',
                          background: userInfo?.profile?.profile_image
                            ? 'transparent'
                            : 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
                          overflow: 'hidden',
                          border: '1.5px solid rgba(255,255,255,0.3)',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
                        }}>
                        {userInfo?.profile?.profile_image ? (
                          <img
                            src={userInfo?.profile?.profile_image}
                            alt="avatar"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <SVGImageComponentReact.IconUser
                            alt="avatar"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        )}
                      </div>
                      <SVGImageComponentReact.IconDropDown
                        style={{
                          marginRight: 'calc(var(--sp-lg) * 1.17924)',
                          height: 'calc(var(--fs-xs) * 1.132075)',
                          width: 'calc(var(--fs-xs) * 1.132075)',
                          color: 'rgba(255,255,255,0.7)',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </div>
                  </div>
                </Dropdown>
              </Col>
            </Header>
            {this.state.showErrorBar &&
              Object.values(ROLES).includes(this.props.userInfo?.user?.role) && (
                <SubscriptionErrorBar
                  message={this.getSubscriptionErrorMessage()}
                  onDismiss={this.handleDismissErrorBar}
                  offsetTop={heightOfHeader}
                />
              )}
            <Content
              id={LocalStorage.DEFINE_KEY.MAIN_APP_ID}
              className={'content-main'}
              style={{
                backgroundColor: '#fff',
                marginLeft: collapsed
                  ? 0
                  : i18n.language === 'es'
                  ? 'calc(var(--fs-xl)*14.56)'
                  : 'calc(var(--fs-xl)*10.849)',
                marginTop: heightOfHeader
              }}>
              {this.props.children}
              <ChangePassword
                showChangePassword={this.state.showChangePassword}
                closeModal={this.closeChangePasswordModal}
              />
            </Content>
          </Layout>
        </Layout>
        <LogoutModal
          visible={this.state.openLogoutModal}
          handleCancel={this.handleCloseLogoutModal}
          handleOk={() => {
            this.logout();
          }}
        />
        <TwoFactorAuthModal
          visible={this.state.show2FAModal}
          handleCancel={this.close2FAModal}
          handleOk={this.confirm2FAToggle}
          isEnabling={!is2FAEnabled}
          loading={this.state.is2FALoading}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    userDetail: getValueFromObjectByKeys(state, ['User', 'userDetail']),
    userInfo: getValueFromObjectByKeys(state, ['User', 'userInfo']),
    vendor: getValueFromObjectByKeys(state, ['User', 'vendor']),
    listNotification: getValueFromObjectByKeys(state, ['User', 'notifications', 'results']),
    notificationsObj: getValueFromObjectByKeys(state, ['User', 'notifications']),
    notifications: state.notifications,
    whitelabel: getValueFromObjectByKeys(state, ['User', 'branding']),
    permissions: state?.permissions?.permissions || []
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getUserProfile: (id) => {
      return dispatch(UserAction.getUserProfile(id));
    },
    saveUserNotification: (data) => {
      return dispatch(UserAction.saveUserNotification(data));
    },
    getVendor: (query) => {
      return dispatch(UserAction.getVendor(query));
    },
    getUnreadNotifCount: () => {
      return dispatch(UserAction.getUnreadNotifCount());
    },
    updateUnreadNotifCount: (data) => {
      return dispatch(UserAction.updateUnreadNotifCount(data));
    },
    getNotifications: (vendor_notes, page = 1) => {
      return dispatch(UserAction.getNotifications({ vendor_notes, page, page_size: 20 }));
    },
    toggle2FA: (params) => {
      return dispatch(UserAction.toggle2FA(params));
    },
    resetAllPages: () => {
      sessionStorage.setItem('filters', JSON.stringify({}));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withNavigate(LayoutApp));
